import {
  buildPreviewOrderSummary,
  createPreviewPatient,
  previewPaymentResult,
} from "./previewSimulation";
import {
  JOURNEY_BOUNDARY_ERROR,
  JOURNEY_BOUNDARY_MODE,
  JOURNEY_PREVIEW_KIND,
} from "./journeyConstants";
import { buildJourneyProjection } from "./journeyProjection";

export class JourneyBoundaryError extends Error {
  constructor(code, details) {
    super(details);
    this.name = "JourneyBoundaryError";
    this.code = code;
  }
}

function requireLiveResolver(liveResolver) {
  if (typeof liveResolver !== "function") {
    throw new JourneyBoundaryError(
      JOURNEY_BOUNDARY_ERROR.liveResolverRequired,
      "The live journey effect resolver is required.",
    );
  }
  return liveResolver;
}

function previewCompletion({ questionnaire, responses, paymentData }) {
  if (!paymentData) {
    const checkout = {
      preview: true,
      order_summary: buildPreviewOrderSummary(questionnaire, responses),
    };
    return {
      success: true,
      preview: true,
      payment_required: true,
      side_effects_disabled: true,
      checkout_ready: checkout,
    };
  }
  return {
    success: true,
    preview: true,
    side_effects_disabled: true,
    responses,
  };
}

function createLiveBoundary() {
  const identityAdapter = Object.freeze({
    mode: JOURNEY_BOUNDARY_MODE.live,
    resolve(_details, liveResolver) {
      return requireLiveResolver(liveResolver)();
    },
  });
  const effectAdapter = Object.freeze({
    mode: JOURNEY_BOUNDARY_MODE.live,
    allowsProtectedEffects: true,
    assertAllowed() {
      return true;
    },
    complete(_context, liveResolver) {
      return requireLiveResolver(liveResolver)();
    },
  });
  return Object.freeze({
    mode: JOURNEY_BOUNDARY_MODE.live,
    isPreview: false,
    allowsProtectedEffects: true,
    projectJourney: buildJourneyProjection,
    identityAdapter,
    effectAdapter,
  });
}

function createPreviewBoundary(previewCapabilityToken) {
  const identityAdapter = Object.freeze({
    mode: JOURNEY_BOUNDARY_MODE.preview,
    resolve(details) {
      return createPreviewPatient(details);
    },
  });
  const effectAdapter = Object.freeze({
    mode: JOURNEY_BOUNDARY_MODE.preview,
    allowsProtectedEffects: false,
    complete: previewCompletion,
    assertAllowed(effectName) {
      throw new JourneyBoundaryError(
        JOURNEY_BOUNDARY_ERROR.previewEffectForbidden,
        `Preview cannot invoke protected effect '${effectName}'.`,
      );
    },
  });
  return Object.freeze({
    mode: JOURNEY_BOUNDARY_MODE.preview,
    previewKind: JOURNEY_PREVIEW_KIND.capability,
    isPreview: true,
    allowsProtectedEffects: false,
    capabilityToken: previewCapabilityToken || null,
    projectJourney: buildJourneyProjection,
    identityAdapter,
    effectAdapter,
    paymentAdapter: Object.freeze({
      gateway: "preview",
      getPaymentData: previewPaymentResult,
    }),
  });
}

export const createJourneyBoundary = ({
  previewCapabilityToken,
} = {}) => {
  if (!previewCapabilityToken) return createLiveBoundary();
  return createPreviewBoundary(previewCapabilityToken);
};
